import { WarningAltFilled20 } from '@carbon/icons-react';
import { OktaContext } from '@okta/okta-react';
import { Link, useLocationSearch } from '@rocketlane/rocket-router';
import { Divider } from 'antd';
import { FC, PropsWithChildren, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AuthenticationServiceProvider } from 'api/account';
import { LinkText, UserInfo } from 'components/atoms/form-elements';
import {
  BackgroundColor,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { Color } from 'components/colors';
import { CustomerLogin } from 'components/molecules/session-setup/customer-login-link-generate';
import { BodyShort01, Label01, Text01 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { useIsSamlMandatory } from '../saml';
import './login.css';
import { CustomSignInWidget } from './custom-signin-widget';
import { ForgotPassword } from './forgot-password';
import {
  Container,
  Heading,
  HeadingContainer,
  HelperTitle,
  TextContainer,
  WelcomeHeading,
} from './login-layout';
import { SsoSamlButton } from './sso-saml-button';
import { useCognitoAuth } from './use-cognito-auth';
import { useLoginSsoCallbackError } from './use-login-sso-callback-error';

const OktaSignInWidget = lazyWithRetry(() =>
  import('./okta-signin-widget').then(({ SignInWidget }) => ({
    default: SignInWidget,
  })),
);

const HelperLink = styled(Link)`
  ${BodyShort01};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  & button {
    height: 50px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;

const StyledDivider = styled(Divider)`
  span {
    transform: translateY(50%);
    font-size: var(--font-size-sm);
    color: var(--gray-50);
  }
`;

const WidgetContainer = styled.div`
  width: fit-content;
  position: relative;
`;

const SsoCallbackErrorAlert = styled.div<{ color: Color }>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: 0 0 var(--spacing-05) 0;
  max-width: 400px;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    flex-shrink: 0;
    fill: var(--red-50);
  }
`;

const StyledSignInWidget = styled(OktaSignInWidget)`
  #okta-sign-in {
    margin-bottom: 0 !important;
    &.auth-container {
      &.main-container {
        box-shadow: none;
        border: none;
        margin-top: 0;
      }
      * {
        font-family: var(--font-family) !important;
      }
    }
    .auth-content {
      padding: 0;
    }
    .auth-footer {
      display: none;
    }
    label {
      ${Label01};
    }

    #okta-sign-in .o-form .input-fix {
      background: var(--gray-10) !important;
    }
    .o-form-fieldset {
      margin-bottom: var(--spacing-05) !important;

      :nth-child(1) {
        margin-bottom: 0px !important;
      }
      :nth-child(2) {
        margin-bottom: var(--spacing-03) !important;
      }
    }

    .o-form-content {
      padding-bottom: 0 !important;
    }

    .o-form-button-bar {
      padding-bottom: var(--spacing-03);
    }

    .o-form .okta-form-input-field {
      width: 100%;
      padding: 0 var(--spacing-05);
      ${InputBorder};
      ${BodyShort01};
      ${Text01};
      ${BackgroundColor};
      height: var(--input-height-lg);

      &.focused-input {
        ${InputFocus};
      }
    }

    /* For signin button*/
    #okta-signin-submit.button.button-primary {
      text-align: left;
      border-radius: 0;
      border-color: var(--blue-60);
      background: var(--blue-60);
      color: var(--white-100);
    }
  }

  .custom-checkbox {
    color: var(--gray-80) !important;
  }

  #okta-sign-in .focused-input {
    box-shadow: none;
  }
  #okta-sign-in .social-auth-button {
    padding: 0 0 0 var(--spacing-05);
    background: var(--blue-60);
    color: var(--white-100);
  }
`;

export const LoginPage: FC<PropsWithChildren<unknown>> = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();
  const isSamlMandate = useIsSamlMandatory();
  const search = useLocationSearch();

  const params = new URLSearchParams(search);
  const customerParam = params.get('customer');
  const partnerParam = params.get('partner');

  const isSamlEnabled = useMemo(
    () => !!(data?.okta.idps ?? []).filter((idp) => idp.enabled).length,
    [data?.okta.idps],
  );

  const isCognitoEnabled =
    data?.okta?.authenticationServiceProvider ===
    AuthenticationServiceProvider.COGNITO;

  const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({ data });

  const oktaAuth = useContext(OktaContext)?.oktaAuth;
  const ssoCallbackErrorMessage = useLoginSsoCallbackError();

  return isLoading ? (
    <PageLoading />
  ) : data && !isError ? (
    <Layout>
      {customerParam ? (
        <CustomerLogin />
      ) : (
        <Container>
          {ssoCallbackErrorMessage && (
            <SsoCallbackErrorAlert color="red">
              <WarningAltFilled20 />
              {ssoCallbackErrorMessage}
            </SsoCallbackErrorAlert>
          )}
          <HeadingContainer>
            {data && <CompanyLogo data={data.company} />}
            <TextContainer>
              <WelcomeHeading>
                <FormattedMessage id="welcome" />
              </WelcomeHeading>
              <Heading>
                <FormattedMessage
                  id="login.title"
                  values={{
                    companyName: data.company.companyName,
                  }}
                />
              </Heading>
            </TextContainer>
          </HeadingContainer>

          {!customerParam &&
            !partnerParam &&
            isSamlEnabled &&
            !isSamlMandate && (
              <ButtonContainer>
                {(data.okta.idps ?? [])
                  .filter((idp) => idp.enabled)
                  .map((idp) => {
                    return (
                      <SsoSamlButton
                        key={idp?.idpId}
                        idp={idp}
                        googleSSOHandler={googleSSOHandler}
                        samlFlowHandler={samlFlowHandler}
                        isCognitoEnabled={isCognitoEnabled}
                        idpId={idp.idpId}
                        oktaAuth={oktaAuth}
                      />
                    );
                  })}

                <StyledDivider orientation="center">
                  <FormattedMessage id="sign_in_with_your_email" />
                </StyledDivider>
              </ButtonContainer>
            )}

          {partnerParam && (
            <HelperTitle>
              <FormattedMessage id="signing_in_as_partner" />
            </HelperTitle>
          )}

          {!customerParam &&
            (isCognitoEnabled ? (
              <CustomSignInWidget data={data} />
            ) : (
              <WidgetContainer>
                <StyledSignInWidget
                  oktaMetaData={data.okta}
                  isSamlMandatory={isSamlMandate}
                  isSamlEnabled={isSamlEnabled}
                  isPartner={partnerParam}
                />

                <ForgotPassword isSamlMandate={isSamlMandate} />
              </WidgetContainer>
            ))}

          {!customerParam && !partnerParam && (
            <LinksContainer>
              <HelperLink to={`${RoutePaths.login}?customer=true`}>
                <FormattedMessage
                  id="are_you_a_customer"
                  values={{
                    companyName: data.company.companyName,
                  }}
                />
              </HelperLink>{' '}
              {isSamlMandate && (
                <>
                  /{' '}
                  <HelperLink to={`${RoutePaths.login}?partner=true`}>
                    <FormattedMessage
                      id="are_you_a_partner"
                      values={{
                        companyName: data.company.companyName,
                      }}
                    />
                  </HelperLink>
                </>
              )}
            </LinksContainer>
          )}

          {customerParam || partnerParam ? (
            <UserInfo>
              <FormattedMessage
                id="forgot_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={RoutePaths.login}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          ) : (
            <></>
          )}
        </Container>
      )}
    </Layout>
  ) : (
    <></>
  );
};
